import { useState, useEffect } from 'react';
import { FileText, Calendar, Plus, X, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, Pencil, Trash2 } from 'lucide-react';
import { useAlarmReport } from '../contexts/AlarmReportContext';
import { apiFetch, getUser } from '../api/client';

// Format 'YYYY/MM/DD HH:MM:SS' Eastern timestamps for display.
function fmtEastern(ts: string | Date, showDate = true, showTime = true): string {
  const s = ts instanceof Date ? ts.toISOString().replace('T', ' ').substring(0, 19) : (ts || '');
  if (!s) return '';
  const [datePart, timePart = '00:00:00'] = s.split(' ');
  const sep = datePart.includes('/') ? '/' : '-';
  const [y, m, d] = datePart.split(sep).map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12  = hh % 12 || 12;
  if (showDate && showTime) return `${months[m-1]} ${d}, ${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
  if (showDate)             return `${months[m-1]} ${d}, ${y}`;
  return `${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
}

function severityLabel(s: string | undefined): string {
  return s === '1' ? '1 — Immediate Action' :
         s === '2' ? '2 — Schedule Action' :
         s === '3' ? '3 — Trend Closely' :
         s === '4' ? '4 — Trend Normally' :
         s === '5' ? '5 — No fault found, alarm adjusted' :
         s === '6' ? '6 — No fault found, no action taken' :
         (s || '—');
}

function severityColor(s: string | undefined): string {
  return s === '1' ? 'bg-red-100 text-red-700' :
         s === '2' ? 'bg-orange-100 text-orange-700' :
         s === '3' ? 'bg-yellow-100 text-yellow-700' :
         s === '4' ? 'bg-gray-100 text-gray-600' :
         s === '5' ? 'bg-blue-100 text-blue-700' :
         s === '6' ? 'bg-teal-100 text-teal-700' :
         'bg-gray-100 text-gray-600';
}

// Format UTC timestamps (report created_at) as Eastern.
// Appends Z if no timezone offset is present so the browser always treats the value as UTC.
function fmtUtcToEastern(ts: string, longMonth = false): string {
  if (!ts) return '';
  let s = ts.trim().replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    month: longMonth ? 'long' : 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface Report {
  id: string;
  machineId: string;
  machine: string;
  area: string;
  plant: string;
  dbName: string;
  companyName: string;
  date: string;
  severity?: string;
  analysis?: string;
  author?: string;
  observations?: string;
  recommendations?: string;
  plotImage?: string;
}

interface AlarmEvent {
  id: string;
  pointName: string;
  metric: string;
  value: number;
  threshold: number;
  severity: 'medium' | 'high';
  timestamp: Date;
  description: string;
}

interface ReportsProps {
  selectedReportId?: string | null;
  prefillAlarm?: { alarmId: string; machineName: string; metric: string } | null;
  onMachineClick?: (machineId: string, machineName: string, points: never[], imageUrl?: string) => void;
}

// API shapes
interface ApiReport {
  id: number;
  machine_id: string;
  machine_name: string;
  company_name: string;
  area_name: string;
  db_id: number;
  db_name?: string;
  severity: number;
  analysis_type: string;
  observations: string | null;
  recommendations: string | null;
  status: string;
  created_by: string;
  created_at: string;
  linked_alarm_id: number | null;
  plot_image?: string | null;
}

interface ApiMachineItem {
  id: string;
  name: string;
  db_name: string;
  company_name: string;
  area_name: string;
}

function mapApiReport(r: ApiReport): Report {
  return {
    id: String(r.id),
    machineId: String(r.machine_id),
    machine: r.machine_name,
    area: r.area_name,
    plant: r.company_name,
    dbName: r.db_name || '',
    companyName: r.company_name,
    date: (r.created_at || '').substring(0, 10),
    severity: String(r.severity),
    analysis: r.analysis_type,
    author: r.created_by,
    observations: r.observations || '',
    recommendations: r.recommendations || '',
    plotImage: r.plot_image || undefined,
  };
}

const ANALYSIS_TYPES = [
  'Bearing', 'Belt/Sheave', 'Coupling', 'Eccentricity', 'Electrical',
  'Electrical Fluting', 'Lubrication', 'Mechanical Looseness', 'Structural Looseness',
  'Unbalance', 'Mis-Alignment', 'Unknown', 'See Comments',
];

export function Reports({ selectedReportId, prefillAlarm, onMachineClick }: ReportsProps) {
  const isAdmin = getUser()?.role === 'admin';
  const { linkAlarmToReport } = useAlarmReport();

  // Filter state — hierarchy drill-down
  const [filterDb, setFilterDb]         = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterArea, setFilterArea]     = useState('');
  const [filterMachine, setFilterMachine] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('All');

  const [sortColumn, setSortColumn]     = useState<'date' | 'severity'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Modal state
  const [isNewReportOpen, setIsNewReportOpen] = useState(false);
  const [viewingReport, setViewingReport]     = useState<Report | null>(null);
  const [editingReport, setEditingReport]     = useState<Report | null>(null);
  const [deleteTarget, setDeleteTarget]       = useState<Report | null>(null);

  // New/edit form state
  const [formState, setFormState] = useState({
    plant: '', company: '', area: '', machine: '',
    severity: '1', analysis: '', alarm: '',
    observations: '', recommendations: '', plotImage: '',
  });
  const [selectedAlarm, setSelectedAlarm] = useState<AlarmEvent | null>(null);

  // API data
  const [reports, setReports]           = useState<Report[]>([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [apiMachines, setApiMachines]   = useState<ApiMachineItem[]>([]);
  const [apiAlarms, setApiAlarms]       = useState<AlarmEvent[]>([]);

  // Load reports
  useEffect(() => {
    setReportsLoading(true);
    apiFetch<{ reports: ApiReport[] }>('/reports?limit=500')
      .then(data => setReports((data.reports || []).map(mapApiReport)))
      .catch(() => setReports([]))
      .finally(() => setReportsLoading(false));
  }, []);

  // Load machines for form dropdowns
  useEffect(() => {
    apiFetch<ApiMachineItem[]>('/machines')
      .then(data => setApiMachines(data))
      .catch(() => {});
  }, []);

  // Load alarms when machine selection changes (form)
  useEffect(() => {
    if (!formState.machine) { setApiAlarms([]); return; }
    const machine = apiMachines.find(m => m.name === formState.machine);
    if (!machine) { setApiAlarms([]); return; }
    apiFetch<{ events: any[] }>(`/alarms/history?machine_id=${machine.id}&limit=50`)
      .then(data => {
        setApiAlarms((data.events || []).map((e: any) => ({
          id: String(e.id),
          pointName: e.point_name || '',
          metric: (e.unit_label || '').replace(/mm\/s/g, 'in/s'),
          value: e.peak_value ?? 0,
          threshold: e.threshold ?? 0,
          severity: e.severity >= 3 ? 'high' : 'medium',
          timestamp: new Date((e.measurement_at || '').replace(/\//g, '-').replace(' ', 'T')),
          description: `${(e.unit_label || '').replace(/mm\/s/g, 'in/s')} exceeded threshold at ${e.point_name}`,
        })));
      })
      .catch(() => setApiAlarms([]));
  }, [formState.machine, apiMachines]);

  // Auto-open new report from alarm
  useEffect(() => {
    if (!prefillAlarm) return;
    setFormState(prev => ({ ...prev, alarm: prefillAlarm.alarmId, machine: prefillAlarm.machineName }));
    setIsNewReportOpen(true);
  }, [prefillAlarm]);

  // Auto-open report by id
  useEffect(() => {
    if (selectedReportId && reports.length > 0) {
      const r = reports.find(r => r.id === selectedReportId);
      if (r) setViewingReport(r);
    }
  }, [selectedReportId, reports]);

  // ---------------------------------------------------------------------------
  // Derived filter lists (cascade)
  // ---------------------------------------------------------------------------
  const dbNames     = [...new Set(apiMachines.map(m => m.db_name))].sort();
  const companies   = [...new Set(apiMachines
    .filter(m => !filterDb || m.db_name === filterDb)
    .map(m => m.company_name)
    .filter(c => c && c !== (filterDb || ''))
  )].sort();
  const areas       = [...new Set(apiMachines
    .filter(m => (!filterDb || m.db_name === filterDb) && (!filterCompany || m.company_name === filterCompany))
    .map(m => m.area_name)
  )].sort();
  const machineNames = [...new Set(apiMachines
    .filter(m =>
      (!filterDb      || m.db_name      === filterDb) &&
      (!filterCompany || m.company_name === filterCompany) &&
      (!filterArea    || m.area_name    === filterArea)
    )
    .map(m => m.name)
  )].sort();

  // ---------------------------------------------------------------------------
  // Filtered + sorted reports
  // ---------------------------------------------------------------------------
  const filteredReports = reports
    .filter(r => {
      if (filterDb      && r.dbName      !== filterDb)      return false;
      if (filterCompany && r.companyName !== filterCompany) return false;
      if (filterArea    && r.area        !== filterArea)     return false;
      if (filterMachine && r.machine     !== filterMachine)  return false;
      if (filterSeverity !== 'All' && r.severity !== filterSeverity) return false;
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortColumn === 'date') cmp = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (sortColumn === 'severity') cmp = parseInt(a.severity || '0') - parseInt(b.severity || '0');
      return sortDirection === 'asc' ? cmp : -cmp;
    });

  const handleSort = (col: 'date' | 'severity') => {
    if (sortColumn === col) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(col); setSortDirection('desc'); }
  };

  // ---------------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------------
  const resetForm = () => {
    setFormState({ plant: '', company: '', area: '', machine: '', severity: '1', analysis: '', alarm: '', observations: '', recommendations: '', plotImage: '' });
    setSelectedAlarm(null);
  };

  const formMachines = apiMachines.filter(m =>
    (!formState.plant   || m.db_name      === formState.plant) &&
    (!formState.company || m.company_name === formState.company) &&
    (!formState.area    || m.area_name    === formState.area)
  );

  const formCompanies = [...new Set(apiMachines
    .filter(m => !formState.plant || m.db_name === formState.plant)
    .map(m => m.company_name).filter(c => c && c !== formState.plant)
  )];
  const showCompany = formCompanies.length > 0;

  const formAreas = [...new Set(apiMachines
    .filter(m => (!formState.plant || m.db_name === formState.plant) && (!formState.company || m.company_name === formState.company))
    .map(m => m.area_name)
  )];

  // ---------------------------------------------------------------------------
  // CRUD
  // ---------------------------------------------------------------------------
  const handleCreateReport = async () => {
    const machine = apiMachines.find(m => m.name === formState.machine);
    if (!machine) { alert('Please select a machine.'); return; }
    if (!formState.analysis) { alert('Please select an analysis type.'); return; }
    const payload = {
      machine_id: String(machine.id),
      severity: Math.min(6, Math.max(1, parseInt(formState.severity) || 1)),
      analysis_type: formState.analysis,
      observations: formState.observations || null,
      recommendations: formState.recommendations || null,
      linked_alarm_id: formState.alarm ? parseInt(formState.alarm) : null,
      plot_image: formState.plotImage || null,
    };
    try {
      const created = await apiFetch<ApiReport>('/reports', { method: 'POST', body: JSON.stringify(payload) });
      const mapped = mapApiReport(created);
      setReports(prev => [mapped, ...prev]);
      if (formState.alarm) linkAlarmToReport(formState.alarm, mapped.id);
      resetForm();
      setIsNewReportOpen(false);
    } catch (err: any) {
      alert('Failed to save report: ' + (err?.message || JSON.stringify(err)));
    }
  };

  const handleUpdateReport = async () => {
    if (!editingReport) return;
    const payload = {
      severity: parseInt(formState.severity) || 1,
      analysis_type: formState.analysis,
      observations: formState.observations || null,
      recommendations: formState.recommendations || null,
      plot_image: formState.plotImage || null,
    };
    try {
      const updated = await apiFetch<ApiReport>(`/reports/${editingReport.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      const mapped = mapApiReport(updated);
      setReports(prev => prev.map(r => r.id === mapped.id ? mapped : r));
      if (viewingReport?.id === mapped.id) setViewingReport(mapped);
      setEditingReport(null);
      resetForm();
    } catch (err: any) {
      alert('Failed to update report: ' + (err?.message || JSON.stringify(err)));
    }
  };

  const handleDeleteReport = async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`/reports/${deleteTarget.id}`, { method: 'DELETE' });
      setReports(prev => prev.filter(r => r.id !== deleteTarget.id));
      if (viewingReport?.id === deleteTarget.id) setViewingReport(null);
      setDeleteTarget(null);
    } catch (err: any) {
      alert('Failed to delete report: ' + (err?.message || JSON.stringify(err)));
    }
  };

  const openEdit = (report: Report) => {
    setFormState({
      plant: report.dbName || '',
      company: report.companyName || '',
      area: report.area || '',
      machine: report.machine || '',
      severity: report.severity || '1',
      analysis: report.analysis || '',
      alarm: '',
      observations: report.observations || '',
      recommendations: report.recommendations || '',
      plotImage: report.plotImage || '',
    });
    setEditingReport(report);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-gray-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-gray-700" />
              Published Reports
            </h1>
            <p className="text-gray-600 mt-2">All published reports related to machinery in the database</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-gray-900 px-4 py-2 bg-white rounded-lg border border-gray-200">
              {filteredReports.length} Reports
            </div>
            {isAdmin && (
              <button
                onClick={() => { resetForm(); setIsNewReportOpen(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                New Report
              </button>
            )}
          </div>
        </div>

        {/* Hierarchy filter dropdowns */}
        <div className="flex gap-3 flex-wrap">
          <select value={filterDb} onChange={e => { setFilterDb(e.target.value); setFilterCompany(''); setFilterArea(''); setFilterMachine(''); }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm">
            <option value="">All Databases</option>
            {dbNames.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setFilterArea(''); setFilterMachine(''); }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm">
            <option value="">All Companies</option>
            {companies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterArea} onChange={e => { setFilterArea(e.target.value); setFilterMachine(''); }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm">
            <option value="">All Areas</option>
            {areas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filterMachine} onChange={e => setFilterMachine(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm">
            <option value="">All Machines</option>
            {machineNames.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm">
            <option value="All">All Severities</option>
            <option value="1">1 — Immediate Action</option>
            <option value="2">2 — Schedule Action</option>
            <option value="3">3 — Trend Closely</option>
            <option value="4">4 — Trend Normally</option>
            <option value="5">5 — No fault found, alarm adjusted</option>
            <option value="6">6 — No fault found, no action taken</option>
          </select>
          {(filterDb || filterCompany || filterArea || filterMachine || filterSeverity !== 'All') && (
            <button onClick={() => { setFilterDb(''); setFilterCompany(''); setFilterArea(''); setFilterMachine(''); setFilterSeverity('All'); }}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Reports Table */}
      {reportsLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center text-gray-400">Loading reports…</div>
      ) : filteredReports.length > 0 ? (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-gray-700">
                    <button onClick={() => handleSort('date')} className="flex items-center gap-1 hover:text-gray-900 transition-colors">
                      Date
                      {sortColumn === 'date' ? (sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />) : <ArrowUpDown className="w-4 h-4 text-gray-400" />}
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-gray-700">Machine</th>
                  <th className="px-6 py-4 text-left text-gray-700">Location</th>
                  <th className="px-6 py-4 text-left text-gray-700">
                    <button onClick={() => handleSort('severity')} className="flex items-center gap-1 hover:text-gray-900 transition-colors">
                      Severity
                      {sortColumn === 'severity' ? (sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />) : <ArrowUpDown className="w-4 h-4 text-gray-400" />}
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-gray-700">Analysis</th>
                  <th className="px-6 py-4 text-left text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 text-gray-600 text-sm whitespace-nowrap">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {fmtUtcToEastern(report.date)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {onMachineClick ? (
                        <button
                          onClick={() => onMachineClick(report.machineId, report.machine, [], undefined)}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm transition-colors text-left"
                        >
                          {report.machine}
                        </button>
                      ) : (
                        <span className="text-gray-900 font-medium text-sm">{report.machine}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{report.companyName} → {report.area}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityColor(report.severity)}`}>
                        {severityLabel(report.severity)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{report.analysis}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewingReport(report)}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          View
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => openEdit(report)}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit report"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(report)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete report"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-12 border border-gray-200 shadow-sm text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-gray-900 mb-2">No reports found</h3>
          <p className="text-gray-600">Try adjusting your filter criteria</p>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* New Report Modal                                                     */}
      {/* ------------------------------------------------------------------ */}
      {isNewReportOpen && (
        <ReportFormModal
          title="Create New Report"
          formState={formState}
          setFormState={setFormState}
          apiMachines={apiMachines}
          formMachines={formMachines}
          formCompanies={formCompanies}
          formAreas={formAreas}
          showCompany={showCompany}
          apiAlarms={apiAlarms}
          selectedAlarm={selectedAlarm}
          setSelectedAlarm={setSelectedAlarm}
          onSubmit={handleCreateReport}
          onCancel={() => { setIsNewReportOpen(false); resetForm(); }}
          submitLabel="Create Report"
        />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Edit Report Modal                                                    */}
      {/* ------------------------------------------------------------------ */}
      {editingReport && (
        <ReportFormModal
          title={`Edit Report — ${editingReport.machine}`}
          formState={formState}
          setFormState={setFormState}
          apiMachines={apiMachines}
          formMachines={formMachines}
          formCompanies={formCompanies}
          formAreas={formAreas}
          showCompany={showCompany}
          apiAlarms={apiAlarms}
          selectedAlarm={selectedAlarm}
          setSelectedAlarm={setSelectedAlarm}
          onSubmit={handleUpdateReport}
          onCancel={() => { setEditingReport(null); resetForm(); }}
          submitLabel="Save Changes"
          editMode
        />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* View Report Modal                                                    */}
      {/* ------------------------------------------------------------------ */}
      {viewingReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-gray-900 flex items-center gap-3">
                <FileText className="w-6 h-6 text-gray-700" />
                {viewingReport.machine}
              </h2>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <>
                    <button onClick={() => { openEdit(viewingReport); setViewingReport(null); }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setDeleteTarget(viewingReport); setViewingReport(null); }}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-400 hover:text-red-600" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button onClick={() => setViewingReport(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Database</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm">{viewingReport.dbName || '—'}</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Company / Area</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm">{viewingReport.companyName} → {viewingReport.area}</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Machine</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm">
                    {onMachineClick ? (
                      <button onClick={() => { setViewingReport(null); onMachineClick(viewingReport.machineId, viewingReport.machine, [], undefined); }}
                        className="text-blue-600 hover:underline font-medium">
                        {viewingReport.machine}
                      </button>
                    ) : (
                      <span className="text-gray-900">{viewingReport.machine}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Severity</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm">
                    {severityLabel(viewingReport.severity)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Analysis</label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm">{viewingReport.analysis || '—'}</div>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Observations</label>
                <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm min-h-[80px] whitespace-pre-wrap">
                  {viewingReport.observations || 'No observations provided.'}
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Recommendations</label>
                <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 text-sm min-h-[80px] whitespace-pre-wrap">
                  {viewingReport.recommendations || 'No recommendations provided.'}
                </div>
              </div>
              {viewingReport.plotImage && (
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Plot</label>
                  <img src={viewingReport.plotImage} alt="Report plot" className="w-full rounded-lg border border-gray-200 bg-gray-50 object-contain max-h-96" />
                </div>
              )}
              <div className="text-xs text-gray-400 pt-1">Created by {viewingReport.author} on {fmtUtcToEastern(viewingReport.date, true)}</div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation Modal                                            */}
      {/* ------------------------------------------------------------------ */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-gray-900 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                Delete Report
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700">Are you sure you want to permanently delete the report for <span className="font-semibold">{deleteTarget.machine}</span>? This cannot be undone.</p>
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleDeleteReport} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared form modal (used by both Create and Edit)
// ---------------------------------------------------------------------------
interface ReportFormModalProps {
  title: string;
  formState: any;
  setFormState: (s: any) => void;
  apiMachines: ApiMachineItem[];
  formMachines: ApiMachineItem[];
  formCompanies: string[];
  formAreas: string[];
  showCompany: boolean;
  apiAlarms: AlarmEvent[];
  selectedAlarm: AlarmEvent | null;
  setSelectedAlarm: (a: AlarmEvent | null) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: string;
  editMode?: boolean;
}

function ReportFormModal({
  title, formState, setFormState,
  apiMachines, formMachines, formCompanies, formAreas, showCompany,
  apiAlarms, selectedAlarm, setSelectedAlarm,
  onSubmit, onCancel, submitLabel, editMode = false,
}: ReportFormModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-gray-900 flex items-center gap-3">
            <FileText className="w-6 h-6 text-gray-700" />
            {title}
          </h2>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Plant / Company / Area / Machine */}
          <div className={`grid grid-cols-1 gap-4 ${showCompany ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            <div>
              <label className="block text-gray-700 mb-2 text-sm">Database</label>
              <select
                value={formState.plant}
                disabled={editMode}
                onChange={e => setFormState({ ...formState, plant: e.target.value, company: '', area: '', machine: '' })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
              >
                <option value="">Select</option>
                {[...new Set(apiMachines.map(m => m.db_name))].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            {showCompany && (
              <div>
                <label className="block text-gray-700 mb-2 text-sm">Company</label>
                <select
                  value={formState.company}
                  disabled={editMode}
                  onChange={e => setFormState({ ...formState, company: e.target.value, area: '', machine: '' })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
                >
                  <option value="">Select</option>
                  {formCompanies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-gray-700 mb-2 text-sm">Area</label>
              <select
                value={formState.area}
                disabled={editMode}
                onChange={e => setFormState({ ...formState, area: e.target.value, machine: '' })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
              >
                <option value="">Select</option>
                {formAreas.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 mb-2 text-sm">Machine</label>
              <select
                value={formState.machine}
                disabled={editMode}
                onChange={e => setFormState({ ...formState, machine: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
              >
                <option value="">Select</option>
                {formMachines.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
            </div>
          </div>

          {/* Severity */}
          <div>
            <label className="block text-gray-700 mb-2 text-sm">Severity</label>
            <select value={formState.severity} onChange={e => setFormState({ ...formState, severity: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="1">1 — High Severity (Immediate Action)</option>
              <option value="2">2 — Schedule Action</option>
              <option value="3">3 — Trend Closely</option>
              <option value="4">4 — Trend Normally</option>
              <option value="5">5 — No fault found, alarm adjusted</option>
              <option value="6">6 — No fault found, no action taken</option>
            </select>
          </div>

          {/* Analysis */}
          <div>
            <label className="block text-gray-700 mb-2 text-sm">Analysis</label>
            <select value={formState.analysis} onChange={e => setFormState({ ...formState, analysis: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="">Select Analysis Type</option>
              {ANALYSIS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Linked alarm — only on create */}
          {!editMode && formState.machine && apiAlarms.length > 0 && (
            <div>
              <label className="block text-gray-700 mb-2 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                Link to Alarm (optional)
              </label>
              <select value={formState.alarm}
                onChange={e => {
                  setFormState({ ...formState, alarm: e.target.value });
                  setSelectedAlarm(apiAlarms.find(a => a.id === e.target.value) || null);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                <option value="">None</option>
                {apiAlarms.map(alarm => {
                  const unit = alarm.metric === 'Velocity' ? 'in/s' : alarm.metric === 'Temperature' ? '°F' : "G's";
                  return (
                    <option key={alarm.id} value={alarm.id}>
                      {fmtEastern(alarm.timestamp)} — {alarm.pointName} {alarm.metric} ({alarm.value} {unit})
                    </option>
                  );
                })}
              </select>
              {selectedAlarm && (
                <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-gray-700">
                  <span className={`font-semibold ${selectedAlarm.severity === 'high' ? 'text-red-600' : 'text-yellow-600'}`}>
                    {selectedAlarm.severity === 'high' ? 'Critical' : 'Warning'}:
                  </span> {selectedAlarm.description}
                  <p className="text-gray-500 mt-1">Occurred on {fmtEastern(selectedAlarm.timestamp)}</p>
                </div>
              )}
            </div>
          )}

          {/* Observations */}
          <div>
            <label className="block text-gray-700 mb-2 text-sm">Observations</label>
            <textarea value={formState.observations} onChange={e => setFormState({ ...formState, observations: e.target.value })}
              placeholder="Enter observations…" rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm" />
          </div>

          {/* Recommendations */}
          <div>
            <label className="block text-gray-700 mb-2 text-sm">Recommendations</label>
            <textarea value={formState.recommendations} onChange={e => setFormState({ ...formState, recommendations: e.target.value })}
              placeholder="Enter recommendations…" rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm" />
          </div>

          {/* Plot image */}
          <div>
            <label className="block text-gray-700 mb-2 text-sm">Plot Image (optional)</label>
            {formState.plotImage ? (
              <div className="relative border border-gray-200 rounded-lg overflow-hidden">
                <img src={formState.plotImage} alt="Pasted plot" className="w-full max-h-64 object-contain bg-gray-50" />
                <button
                  type="button"
                  onClick={() => setFormState({ ...formState, plotImage: '' })}
                  className="absolute top-2 right-2 p-1 bg-white rounded-full shadow hover:bg-red-50 transition-colors"
                  title="Remove image"
                >
                  <X className="w-4 h-4 text-red-500" />
                </button>
              </div>
            ) : (
              <div
                tabIndex={0}
                onPaste={e => {
                  const items = e.clipboardData?.items;
                  if (!items) return;
                  for (const item of Array.from(items)) {
                    if (item.type.startsWith('image/')) {
                      const blob = item.getAsFile();
                      if (!blob) continue;
                      const reader = new FileReader();
                      reader.onload = ev => setFormState({ ...formState, plotImage: ev.target?.result as string });
                      reader.readAsDataURL(blob);
                      e.preventDefault();
                      break;
                    }
                  }
                }}
                className="w-full px-3 py-8 border-2 border-dashed border-gray-200 rounded-lg text-center text-sm text-gray-400 focus:outline-none focus:border-blue-400 cursor-pointer hover:border-gray-300 transition-colors"
              >
                <div className="flex flex-col items-center gap-1">
                  <span>Paste plot image here</span>
                  <span className="text-xs text-gray-300">Click this area, then Ctrl+V / Cmd+V</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button onClick={onCancel} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">Cancel</button>
          <button onClick={onSubmit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">{submitLabel}</button>
        </div>
      </div>
    </div>
  );
}
